import { useState } from "react";

import { captureLocation, type GeolocationLike } from "../capture/geolocation";
import { defaultIsOnline } from "../capture/use-offline-queue";
import { NOOP_ANALYTICS, type Analytics } from "../lib/analytics";
import { submitTimePunch, type TimePunchRpcClient, type TimePunchType } from "./submit-punch";

// Timeclock self-service panel (ship-dark behind web_timeclock). Online-only BY DESIGN: web
// punches are never queued, and the panel says so instead of pretending. GPS rides along the
// same way scans do — denied is a flagged field reality, never a rejection. Session-local
// "last punch" memory only; the supervisor console keeps the record.

const PUNCHES: ReadonlyArray<{ type: TimePunchType; label: string }> = [
  { type: "clock_in", label: "Clock in" },
  { type: "break_start", label: "Start break" },
  { type: "break_end", label: "End break" },
  { type: "clock_out", label: "Clock out" },
];

interface PunchStatus {
  tone: "ok" | "muted" | "error";
  text: string;
}

export function TimeclockPanel({
  client,
  accountId,
  deviceId,
  routeAssignmentId,
  analytics = NOOP_ANALYTICS,
  isOnline = defaultIsOnline,
  geolocation,
}: {
  client: TimePunchRpcClient;
  accountId: string;
  deviceId: string;
  routeAssignmentId: string | null;
  analytics?: Analytics;
  /** Test seam; defaults to navigator.onLine. */
  isOnline?: () => boolean;
  /** Test seam; defaults to navigator.geolocation. */
  geolocation?: GeolocationLike;
}) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<PunchStatus | null>(null);
  const [lastPunch, setLastPunch] = useState<string | null>(null);

  async function punch(type: TimePunchType, label: string): Promise<void> {
    setStatus(null);
    if (!isOnline()) {
      setStatus({
        tone: "muted",
        text: "You're offline — the timeclock needs a connection. Punches are not queued on web.",
      });
      return;
    }
    setBusy(true);
    const fix = await captureLocation({ geolocation });
    const result = await submitTimePunch(client, {
      accountId,
      punchType: type,
      deviceId,
      appVersion: __APP_VERSION__,
      routeAssignmentId,
      lat: fix.lat,
      lng: fix.lng,
      gpsAccuracyMeters: fix.accuracyMeters,
      gpsPermission: fix.permission,
    });
    setBusy(false);
    if (result.ok) {
      const at = new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      setStatus({ tone: "ok", text: `${label} recorded · ${at}` });
      setLastPunch(`${label} · ${at}`);
      analytics.capture("timeclock_punch", { appVersion: __APP_VERSION__, punchType: type });
    } else {
      setStatus({ tone: "error", text: result.reason });
    }
  }

  return (
    <section className="timeclockpanel" aria-label="Timeclock">
      <div className="timeclockpanel__head">
        <h2 className="timeclockpanel__title">Timeclock</h2>
        {lastPunch !== null ? <span className="timeclockpanel__last">Last this session: {lastPunch}</span> : null}
      </div>
      <div className="timeclockpanel__buttons">
        {PUNCHES.map((entry) => (
          <button
            key={entry.type}
            className="timeclockpanel__button"
            type="button"
            disabled={busy}
            onClick={() => void punch(entry.type, entry.label)}
          >
            {busy ? "Recording…" : entry.label}
          </button>
        ))}
      </div>
      {status !== null ? (
        <p
          className={`timeclockpanel__status timeclockpanel__status--${status.tone}`}
          role={status.tone === "error" ? "alert" : "status"}
        >
          {status.text}
        </p>
      ) : null}
      <p className="timeclockpanel__scope">
        Punch history lives in the supervisor console — this panel shows this session only.
      </p>
    </section>
  );
}
